import React, { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import * as L from "leaflet";
import "leaflet.markercluster";
import { iconForBucket } from "./helpers/icons";

type P = {
  points: SimplifiedCsdsListInfo[];
  bucketOf: (p: SimplifiedCsdsListInfo) => 0 | 1 | 2 | 3 | 4;
  disableAt?: number;
  equipmentBaseUrl: string;
};

export const ClusterLayer: React.FC<P> = ({ points, bucketOf, disableAt = 13, equipmentBaseUrl }) => {
  const map = useMap();
  const clusterRef = useRef<L.MarkerClusterGroup | null>(null);

  useEffect(() => {
    if (!map) return;

    const group = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      zoomToBoundsOnClick: true,
      removeOutsideVisibleBounds: true,
      chunkedLoading: true,
      disableClusteringAtZoom: disableAt,
      iconCreateFunction: (cluster) => {
        const children = cluster.getAllChildMarkers() as L.Marker[];
        const counts = { 1: 0, 2: 0, 3: 0, 4: 0 } as Record<1 | 2 | 3 | 4, number>;
        for (const m of children) {
          const b = ((m as any).__bucket ?? 1) as 1 | 2 | 3 | 4;
          counts[b] += 1;
        }
        const worst = (counts[4] ? 4 : counts[3] ? 3 : counts[2] ? 2 : 1) as 1 | 2 | 3 | 4;
        const count = cluster.getChildCount();
        return L.divIcon({
          html: `<div><span>${count}</span></div>`,
          className: `marker-cluster custom-cluster b${worst}`,
          iconSize: L.point(40, 40),
        });
      },
    });

    group.on("clusterclick", (e: any) => e.layer.zoomToBounds({ padding: [20, 20] }));
    clusterRef.current = group;
    map.addLayer(group);

    return () => { map.removeLayer(group); clusterRef.current = null; };
  }, [map, disableAt]);

  useEffect(() => {
    const group = clusterRef.current;
    if (!group) return;

    group.clearLayers();

    for (const p of points) {
      if (p.latitude == null || p.longitude == null) continue;

      const b = (bucketOf(p) || 1) as 1 | 2 | 3 | 4;
      const marker = L.marker([p.latitude, p.longitude], { icon: iconForBucket(b) });
      (marker as any).__bucket = b;

      // Build popup DOM to wire copy interaction
      const wrap = document.createElement("div");
      wrap.className = "popup-card";
      wrap.innerHTML = `
        <div>
          <strong>Equipment:</strong><br/>
          <span class="equip-text"></span>
          <button type="button" class="copy-equip" title="Copy">
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M16 1H4c-1.1 0-2 .9-2 2v12h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" fill="currentColor"/>
            </svg>
          </button>
          <br/><br/>
          <a class="map-tooltip-link" target="_blank" rel="noreferrer"
             href="${equipmentBaseUrl}${encodeURIComponent(String(p.equipment ?? ""))}/communication-history">
             Communication History
          </a>
        </div>
      `;
      (wrap.querySelector(".equip-text") as HTMLElement | null)!.textContent = String(p.equipment ?? "");

      const btn = wrap.querySelector(".copy-equip") as HTMLButtonElement | null;
      if (btn) {
        L.DomEvent.disableClickPropagation(btn);
        btn.addEventListener("click", async (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          try {
            await navigator.clipboard.writeText(String(p.equipment ?? ""));
            btn.title = "Copied!";
            btn.classList.add("copied");
            setTimeout(() => { btn.title = "Copy"; btn.classList.remove("copied"); }, 900);
          } catch (err) {
            console.error("Copy failed:", err);
            alert("Could not copy to clipboard.");
          }
        });
      }

      marker.bindPopup(wrap, { maxWidth: 260 });
      group.addLayer(marker);
    }
  }, [points, bucketOf, equipmentBaseUrl]);

  return null;
};
